import { useState } from 'react';
import { useChangeSessionTutor } from '../../api/admin';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { TutorPicker } from '../../components/TutorPicker';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { formatSlotRange } from '../../lib/format';
import type { Session } from '../../types/api';

/**
 * Whether this lesson can still change hands: scheduled, and not yet
 * started. Afterwards there is nothing left to hand over, and the API
 * refuses it too.
 */
export function canChangeTutor(session: Session): boolean {
  return session.status === 'scheduled' && new Date(session.startTime).getTime() > Date.now();
}

/**
 * Moving one lesson to another tutor.
 *
 * The time never moves here — only who teaches it — because an operator
 * reaching for this is usually covering for someone who has dropped out an
 * hour before the lesson, and a family that has already cleared the evening
 * does not want it moved as well.
 *
 * Mounted with `key={session.id}` so each lesson starts from a blank form.
 */
export function ChangeTutorModal({
  session,
  onClose,
}: {
  session: Session;
  onClose: () => void;
}) {
  const change = useChangeSessionTutor();
  const { notify } = useToast();

  const [tutorId, setTutorId] = useState<string>();
  const [tutorName, setTutorName] = useState<string>();
  const [reason, setReason] = useState('');

  const submit = () => {
    if (!tutorId || !reason.trim()) return;
    change.mutate(
      { id: session.id, tutorId, reason: reason.trim() },
      {
        onSuccess: () => {
          notify(
            `${tutorName ?? 'The new tutor'} is taking this lesson. Everyone has been told.`,
            'success',
          );
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title="Change tutor"
      description={`${session.student.fullName} · ${formatSlotRange(session.startTime, session.endTime)}`}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!tutorId || !reason.trim() || change.isPending}
            onClick={submit}
          >
            {change.isPending && <Spinner />}
            Change the tutor
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-ink-3">
          Currently with{' '}
          <span className="font-medium text-ink-2">{session.tutor.fullName}</span>. The
          time, the chat thread and the video room all stay as they are.
        </p>

        {session.learningAppointmentId && (
          <p className="rounded-md border border-rule bg-surface-2 px-3 py-2 text-xs text-ink-2">
            This lesson belongs to a learning plan, so only a teacher already on
            that track can take it. Put someone new on the plan from the plan
            manager first &mdash; that is where the family&rsquo;s agreement is
            recorded.
          </p>
        )}

        {session.subscriptionId && (
          <p className="rounded-md border border-rule bg-surface-2 px-3 py-2 text-xs text-ink-2">
            Only this lesson moves. The bundle keeps its own tutor &mdash; swap that
            from Subscriptions if the rest should follow.
          </p>
        )}

        <TutorPicker
          value={tutorId}
          onChange={(id, name) => {
            setTutorId(id);
            setTutorName(name);
          }}
          subjectSlug={session.subjectRef?.slug}
          excludeTutorId={session.tutor.id}
          label="New tutor"
        />

        <label className="block">
          <span className="block text-xs font-medium text-ink-2">
            Reason{' '}
            <span className="font-normal text-ink-3">
              (required &mdash; the outgoing tutor is shown it, the family is not)
            </span>
          </span>
          <input
            className="input mt-1"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tutor signed off sick for the week"
            maxLength={500}
          />
        </label>

        {change.isError && (
          <p role="alert" className="text-sm text-danger">
            {change.error instanceof ApiError
              ? change.error.message
              : 'Could not change the tutor on this lesson.'}
          </p>
        )}

        <p className="text-xs text-ink-3">
          Both tutors and the family are notified, and the change is written to the
          audit log.
        </p>
      </div>
    </Modal>
  );
}
